import { defineStore } from 'pinia';
import dayjs from 'dayjs';
import {
  featureAnnouncements,
  DEFAULT_EXPIRATION_DAYS,
  DEFAULT_EXPIRATION_CEILING_DAYS,
} from '@/constants/featureAnnouncements';
import { useFeatureFlagsStore } from '@/stores/useFeatureFlags';
import { uselocalStorageGetterSetter } from '@/composables/uselocalStorageGetterSetter';

/**
 * localStorage key for first-eligible dates. Seen/eligibility state is
 * per-browser (not server-side) for v1 — the browser is the per-carrier boundary, so no
 * carrier id is keyed here (consistent with the existing `NEW_FEATURES` pattern). Shape:
 * { [announcementId]: isoDateString }
 */
const FIRST_ELIGIBLE_STORAGE_KEY = 'FEATURE_ANNOUNCEMENT_FIRST_ELIGIBLE';

/**
 * localStorage key for per-card seen state. Same per-browser rationale as
 * FIRST_ELIGIBLE_STORAGE_KEY above: seen state is per-browser, not server-side,
 * for v1. Shape: { [announcementId]: true }
 */
const SEEN_STORAGE_KEY = 'FEATURE_ANNOUNCEMENT_SEEN';

/** True while `now` is strictly before `startDate + days`. Returns false for a missing/invalid start. */
const isWithinWindow = (startDateIso, days) => {
  if (!startDateIso) {
    return false;
  }
  const start = dayjs(startDateIso);
  if (!start.isValid()) {
    return false;
  }
  return dayjs().isBefore(start.add(days, 'day'));
};

export const useFeatureAnnouncementsStore = defineStore('featureAnnouncementsStore', {
  state: () => {
    return {
      announcements: featureAnnouncements,
      // Maps an announcement id to the ISO date it first became eligible in this browser.
      // e.g. { 'instant-book-launch': '2026-07-10T14:32:00.000Z' }
      firstEligibleDates: {},
      // Maps an announcement id to `true` once it has been marked seen in this browser.
      // e.g. { 'instant-book-launch': true }
      seenIds: {},
      /**
       * A single announcement the carrier asked to read, by clicking it in the What's New
       * panel. When set, the modal shows just that one instead of the login batch (see
       * `modalAnnouncements`). Cleared when the modal closes.
       */
      requestedAnnouncementId: null,
    };
  },
  actions: {
    /** Load pinned first-eligible dates from localStorage into state. Call once on app init. */
    hydrateFirstEligibleDates() {
      const { getParsedObject } = uselocalStorageGetterSetter();
      this.firstEligibleDates = getParsedObject(FIRST_ELIGIBLE_STORAGE_KEY) || {};
    },
    persistFirstEligibleDates() {
      const { setObject } = uselocalStorageGetterSetter();
      setObject(FIRST_ELIGIBLE_STORAGE_KEY, this.firstEligibleDates);
    },
    /** Load seen state from localStorage into state. Call once on app init. */
    hydrateSeenIds() {
      const { getParsedObject } = uselocalStorageGetterSetter();
      this.seenIds = getParsedObject(SEEN_STORAGE_KEY) || {};
    },
    persistSeenIds() {
      const { setObject } = uselocalStorageGetterSetter();
      setObject(SEEN_STORAGE_KEY, this.seenIds);
    },
    /**
     * Ask the modal to show one specific announcement — what clicking a What's New panel row
     * does. Overrides the login batch for as long as it's set, so the carrier reads the thing
     * they clicked rather than whatever was queued for login.
     */
    requestAnnouncementInModal(id) {
      this.requestedAnnouncementId = this.getById(id) ? id : null;
    },
    /** Called when the modal closes: the next open should fall back to the login batch. */
    clearRequestedAnnouncement() {
      this.requestedAnnouncementId = null;
    },
    /**
     * Mark a single announcement as seen and persist it. Idempotent: calling it
     * again for an already-seen id is a no-op (no state change, no re-persist).
     */
    markSeen(id) {
      if (this.seenIds[id]) {
        return;
      }
      this.seenIds = { ...this.seenIds, [id]: true };
      this.persistSeenIds();
    },
    /**
     * `publishDate` is the same for every carrier, but a flag-gated announcement isn't
     * actually relevant to a carrier until their flag turns on — which may happen well
     * after `publishDate`. So for gated announcements we pin "first-eligible" per carrier,
     * the first time we see their flag on, instead of reusing the global `publishDate`.
     * That pinned date is what starts their personal 30-day login-push window
     * (`isWithinPerCarrierWindow`) — see `firstEligibleDate`.
     *
     * Idempotent: once pinned, a date is never overwritten, so toggling a flag off and
     * back on doesn't restart the window. Ungated announcements are skipped entirely —
     * they just use `publishDate` directly, nothing to pin.
     */
    recordFirstEligibleDates() {
      const flagsStore = useFeatureFlagsStore();
      const dates = { ...this.firstEligibleDates };
      let changed = false;

      this.announcements.forEach((announcement) => {
        if (!announcement.featureFlagSlug) {
          return; // ungated: uses publishDate, nothing to pin
        }
        if (!flagsStore.getByFlagName(announcement.featureFlagSlug)) {
          return; // flag off: not eligible yet
        }
        if (dates[announcement.id]) {
          return; // already pinned — never reset
        }
        dates[announcement.id] = dayjs().toISOString();
        changed = true;
      });

      if (changed) {
        this.firstEligibleDates = dates;
        this.persistFirstEligibleDates();
      }
    },
  },
  getters: {
    getById() {
      return (id) => this.announcements.find((announcement) => announcement.id === id);
    },
    /**
     * Has this announcement been marked seen in this browser — either because the carrier
     * saw it, or because it was authored with `seenByDefault` (e.g. backfilling an
     * announcement for a feature carriers already know about, so it doesn't get pushed
     * at login or count toward the unread badge).
     */
    isSeen() {
      return (id) => Boolean(this.seenIds[id]) || Boolean(this.getById(id)?.seenByDefault);
    },
    /**
     * Is this announcement eligible for this carrier at all?
     * Ungated announcements are always eligible. A flag-gated announcement is eligible
     * only while its flag is on for the current carrier.
     */
    isEligible() {
      return (id) => {
        const announcement = this.getById(id);
        if (!announcement) {
          return false;
        }
        if (!announcement.featureFlagSlug) {
          return true;
        }
        return Boolean(useFeatureFlagsStore().getByFlagName(announcement.featureFlagSlug));
      };
    },
    /**
     * The date this carrier first became eligible for the announcement:
     * - ungated → the announcement's `publishDate` (same for every carrier)
     * - flag-gated → the pinned date from the first observation of the flag on
     *   (undefined until `recordFirstEligibleDates` has pinned it).
     */
    firstEligibleDate() {
      return (id) => {
        const announcement = this.getById(id);
        if (!announcement) {
          return undefined;
        }
        if (!announcement.featureFlagSlug) {
          return announcement.publishDate;
        }
        return this.firstEligibleDates?.[id];
      };
    },
    /**
     * Still inside the login-push window: 30 days from this carrier's first-eligible date by
     * default. The shorter of the two windows — the announcement outlives it in the panel.
     */
    isWithinPerCarrierWindow() {
      return (id) => {
        const announcement = this.getById(id);
        if (!announcement) {
          return false;
        }
        const days = announcement.expirationDays ?? DEFAULT_EXPIRATION_DAYS;
        return isWithinWindow(this.firstEligibleDate(id), days);
      };
    },
    /**
     * Still inside the announcement's whole lifetime: 365 days from `publishDate` by default,
     * global rather than per-carrier.
     *
     * This is the single answer to "is this announcement available at all" — it governs both
     * the panel list and, as an outer bound, the login push. There is deliberately no separate
     * panel window: an earlier version had one, which meant two names for one concept and two
     * places to change a decision.
     */
    isWithinCeiling() {
      return (id) => {
        const announcement = this.getById(id);
        if (!announcement) {
          return false;
        }
        const days = announcement.expirationCeilingDays ?? DEFAULT_EXPIRATION_CEILING_DAYS;
        return isWithinWindow(announcement.publishDate, days);
      };
    },
    /**
     * Should this be pushed at login right now? Requires eligibility, both windows still open
     * (the 30-day push window and the ceiling that bounds the announcement's whole life), and
     * not yet seen — a seen card never resurfaces on its own.
     *
     * The ceiling matters here for the carrier whose flag turns on long after publish: their
     * first-eligible date is "today", so the push window alone would present a years-old
     * feature as new.
     */
    shouldShowInModal() {
      return (id) =>
        this.isEligible(id) &&
        this.isWithinPerCarrierWindow(id) &&
        this.isWithinCeiling(id) &&
        !this.isSeen(id);
    },
    /**
     * The batch queued for the login modal: everything unseen and inside both expiration
     * windows. This is what gets *pushed* at the carrier, and what `unseenCount` counts.
     */
    loginModalAnnouncements() {
      return this.announcements.filter((announcement) => this.shouldShowInModal(announcement.id));
    },
    /**
     * What the modal shows right now — one list, two sources.
     *
     * A specific announcement the carrier clicked in the What's New panel takes precedence:
     * it's a direct response to a click, whereas the login batch is happy to wait for the next
     * page load. With nothing requested this is just the login batch, so the modal container
     * has a single input regardless of which path opened it.
     */
    modalAnnouncements() {
      const requested = this.requestedAnnouncementId
        ? this.getById(this.requestedAnnouncementId)
        : null;

      return requested ? [requested] : this.loginModalAnnouncements;
    },
    /**
     * Announcements the What's New panel lists: flag-eligible and still inside the ceiling.
     * Seen-state is deliberately ignored — a read announcement stays listed, it just loses its
     * unread dot.
     *
     * The 30-day push window has no bearing here: an announcement stops opening the modal by
     * itself after 30 days but remains selectable for the rest of its life.
     */
    panelAnnouncements() {
      return this.announcements.filter(
        (announcement) => this.isEligible(announcement.id) && this.isWithinCeiling(announcement.id),
      );
    },
    /**
     * How many announcements are queued for the login modal. Reads the login batch, not
     * `modalAnnouncements` — a card the carrier opened from the panel is not a pending
     * announcement and must not count.
     */
    unseenCount() {
      return this.loginModalAnnouncements.length;
    },
    /**
     * How many panel rows are still unread. Drives the What's New badge, so it counts exactly
     * what the panel lists and nothing else.
     *
     * Deliberately not `unseenCount` above: an announcement past its 30-day push window but
     * still inside the ceiling counts as unread here, because it's sitting in the panel waiting
     * to be read. `unseenCount` answers a different question — whether the login modal has
     * anything to show.
     */
    unreadCount() {
      return this.panelAnnouncements.filter((announcement) => !this.isSeen(announcement.id)).length;
    },
  },
});
